import type { PandGebruik, TypeWoning, UserSegment } from './comfortCalc';
import { isResidentialTypeWoning } from './comfortCalc';

export type GebouwOptie = { value: TypeWoning; label: string };

const WONING4: GebouwOptie[] = [
  { value: 'Appartement', label: 'Appartement' },
  { value: 'Tussenwoning', label: 'Tussenwoning' },
  { value: 'Hoekwoning', label: 'Hoekwoning' },
  { value: 'Vrijstaand', label: 'Vrijstaand' },
];

const UTIL_EXTRA: GebouwOptie[] = [
  { value: 'Kantoor_pand', label: 'Kantoorpand' },
  { value: 'Winkel_of_horeca', label: 'Winkel of horeca' },
  { value: 'School_zorg_publiek', label: 'School, zorg of ander publiek gebouw' },
  { value: 'Bedrijfshal_werkplaats', label: 'Bedrijfshal of werkplaats' },
  { value: 'Gemengd_gebouw', label: 'Gemengd gebouw (bijv. wonen en werk)' },
];

const ALLES: GebouwOptie[] = [...WONING4, ...UTIL_EXTRA];

/** Welke gebouwtypes in de lijst staan, hangt af van je doelgroep (en dus vaak van welke regels je meeneemt). */
export function gebouwOptiesVoorSegment(segment: UserSegment): GebouwOptie[] {
  switch (segment) {
    case 'particulier':
    case 'bank_hypotheek':
      return WONING4;
    case 'vve':
      return [
        { value: 'Appartement', label: 'Appartement (eigen woning)' },
        { value: 'Gemengd_gebouw', label: 'Gebouw met meerdere functies / VvE-complex' },
      ];
    case 'woningcorporatie':
      return [
        { value: 'Appartement', label: 'Flat / portiek / galerij' },
        { value: 'Tussenwoning', label: 'Rij- of tussenwoning' },
        { value: 'Hoekwoning', label: 'Hoek- of einwoning' },
        { value: 'Vrijstaand', label: 'Vrijstaand (uitzondering)' },
        { value: 'Gemengd_gebouw', label: 'Gemengd (woning + bijv. winkel)' },
      ];
    case 'gemeente':
      return [
        { value: 'School_zorg_publiek', label: 'School, kinderopvang, zorg of sport' },
        { value: 'Kantoor_pand', label: 'Kantoor of dienstverlening' },
        { value: 'Winkel_of_horeca', label: 'Winkel of horeca' },
        { value: 'Appartement', label: 'Appartementen (bijv. sociaal / gemeentelijk)' },
        { value: 'Gemengd_gebouw', label: 'Overig maatwerk / gemengd' },
      ];
    case 'installateur':
    case 'aannemer':
    case 'vastgoedontwikkelaar':
    case 'projectontwikkelaar':
      return ALLES;
    default:
      return WONING4;
  }
}

export function syncTypeWoningVoorSegment(current: TypeWoning, segment: UserSegment): TypeWoning {
  const allowed = new Set(gebouwOptiesVoorSegment(segment).map((o) => o.value));
  if (allowed.has(current)) return current;
  return gebouwOptiesVoorSegment(segment)[0].value;
}

export type ScanContext = {
  utiliteit: boolean;
  pandWoord: 'huis' | 'pand' | 'gebouw';
  standaardSchilTitel: string;
  standaardSchilSub: string;
  situatieStepTitel: string;
  situatieIntro: string;
  wensenIntro: string;
  ruimtesStepTitel: string;
  ruimtesStepIntro: string;
  gebruikStepTitel: string;
  personenLabel: string;
  personenTip: string;
  postcodeLabel: string;
  gebouwVormLabel: string;
  topbarSituatie: string;
};

export function getScanContext(type_woning: TypeWoning, segment: UserSegment): ScanContext {
  const utiliteit = !isResidentialTypeWoning(type_woning);
  const pandWoord =
    segment === 'gemeente' || segment === 'woningcorporatie'
      ? 'gebouw'
      : utiliteit
        ? 'pand'
        : 'huis';
  const standaardSchilTitel = utiliteit ? 'Standaard voor heel het gebouw' : 'Standaard voor heel je huis';
  const standaardSchilSub = utiliteit
    ? 'Telt voor elke ruimte waar je onderaan “zelfde als standaard” laat staan.'
    : 'Telt voor elke kamer waar je onderaan “zelfde als standaard” laat staan.';
  const situatieStepTitel = utiliteit
    ? 'Stroom, water & wat er al staat'
    : 'Meterkast, water & wat er al hangt';
  const situatieIntro = utiliteit
    ? 'Geen keuring — alleen jouw schatting. Zo sluit het straks aan bij stroom, water en de huidige installatie op locatie.'
    : 'Geen keuring — alleen jouw schatting. Zo sluit het advies aan bij je meterkast, water en je huidige ketel of warmtepomp.';
  const wensenIntro = utiliteit
    ? 'Je voorkeur nemen we mee. Past iets niet bij dit type pand, dan zeggen we dat in gewone taal.'
    : 'Je voorkeur nemen we mee. Past iets niet bij je huis, dan zeggen we dat ook — in gewone taal.';
  const ruimtesStepTitel = utiliteit ? 'Ruimtes & schil (invulbrief)' : 'Kamers & schil (invulbrief)';
  const ruimtesStepIntro = utiliteit
    ? 'Je vult zelf vierkante meters in — geen plattegrond-upload. Onderaan elke ruimte kun je afwijkend glas of zonwering kiezen; laat leeg voor de standaard hierboven.'
    : 'Zoals bij een bureau: je vult zelf oppervlakten in. Onderaan elke kamer kun je afwijkend glas of zonwering zetten; laat leeg voor “zelfde als standaard hieronder”.';
  const gebruikStepTitel = 'Temperatuur, mensen & postcode';
  const personenLabel = utiliteit
    ? 'Hoeveel mensen zijn er ongeveer tegelijk? *'
    : 'Hoeveel mensen wonen er normaal? *';
  const personenTip = utiliteit
    ? 'Denk aan drukste moment (werkuren, lessen, …). Minstens 1, anders kan de scan niet rekenen.'
    : 'Iedereen die er woont telt mee; mensen geven een beetje warmte af in de winter.';
  const postcodeLabel = 'Postcode (eerste vier cijfers) *';
  const gebouwVormLabel =
    segment === 'particulier' || segment === 'bank_hypotheek'
      ? 'Soort woning *'
      : 'Soort gebouw *';
  const topbarSituatie = utiliteit ? 'Locatie' : 'Thuis';
  return {
    utiliteit,
    pandWoord,
    standaardSchilTitel,
    standaardSchilSub,
    situatieStepTitel,
    situatieIntro,
    wensenIntro,
    ruimtesStepTitel,
    ruimtesStepIntro,
    gebruikStepTitel,
    personenLabel,
    personenTip,
    postcodeLabel,
    gebouwVormLabel,
    topbarSituatie,
  };
}

/**
 * Volgorde voor leken: eerst het gebouw, dan hoe je woont (warmte + regio),
 * dan kamers, daarna wat er al hangt, wensen, tot slot controleren.
 */
export function wizardStepTopLabels(ctx: ScanContext): string[] {
  return [
    'Over jou',
    'Gebouw',
    'Wonen & regio',
    ctx.utiliteit ? 'Ruimtes' : 'Kamers',
    ctx.utiliteit ? 'Installatie' : 'Meterkast & ketel',
    'Wensen',
    'Klaar',
  ];
}

export function pandGebruikKeuzes(utiliteit: boolean): { value: PandGebruik; label: string }[] {
  if (!utiliteit) {
    return [
      { value: 'normaal_bewoond', label: 'Gewoon bewoond' },
      { value: 'deels_leeg', label: 'Deels leeg / verbouw' },
      { value: 'heel_leeg', label: 'Helemaal leeg' },
      { value: 'nieuwbouw', label: 'Nieuwbouw' },
    ];
  }
  return [
    { value: 'normaal_bewoond', label: 'Normaal in gebruik' },
    { value: 'deels_leeg', label: 'Deels leeg of verbouw' },
    { value: 'heel_leeg', label: 'Bijna leeg / weinig gebruik' },
    { value: 'nieuwbouw', label: 'Nieuwbouw' },
  ];
}
